import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { publicQuestionsService, PublicQuestion, PublicQuestionList } from '../services/publicQuestionsService';

export const FAQ: React.FC = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<PublicQuestion[]>([]);
  const [myQuestions, setMyQuestions] = useState<PublicQuestionList[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMyQuestions, setLoadingMyQuestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedQuestion, setSelectedQuestion] = useState<PublicQuestion | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchQuestions();
    if (user) {
      fetchMyQuestions();
    }
  }, [user, currentPage]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const data = await publicQuestionsService.getAllQuestions(currentPage);
      setQuestions(data.results);
      // Calculer le nombre de pages (10 par page)
      if (data.count) {
        setTotalCount(data.count);
        setTotalPages(Math.ceil(data.count / 10));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyQuestions = async () => {
    if (!user) return;
    try {
      setLoadingMyQuestions(true);
      const data = await publicQuestionsService.getMyQuestions();
      setMyQuestions(data.questions);
    } catch (error) {
      console.error('Erreur lors du chargement de vos questions:', error);
    } finally {
      setLoadingMyQuestions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: { [key: string]: string } = {};
    if (!formData.question.trim()) {
      newErrors.question = 'La question est requise';
    } else if (formData.question.trim().length < 10) {
      newErrors.question = 'La question doit contenir au moins 10 caractères';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setErrors({});
    setSuccess(false);

    try {
      await publicQuestionsService.createQuestion(formData.question.trim());

      setSuccess(true);
      setFormData({ question: '' });
      setShowForm(false);
      
      fetchQuestions();
      if (user) {
        fetchMyQuestions();
      }

      setTimeout(() => setSuccess(false), 5000);
    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      setErrors({
        submit: error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredQuestions = questions.filter((q) => {
    const search = searchQuery.toLowerCase();
    return (
      q.question.toLowerCase().includes(search) ||
      (q.answer && q.answer.toLowerCase().includes(search))
    );
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const hasAnswer = (question: PublicQuestion) => {
    return !!(question.answer || question.answer_video);
  };

  const openDetailsModal = async (question: PublicQuestion) => {
    try {
      // Récupérer les détails complets de la question
      const fullQuestion = await publicQuestionsService.getQuestionById(question.id);
      setSelectedQuestion(fullQuestion);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      setSelectedQuestion(question);
      setShowDetailsModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50/30 to-green-100/50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-500 to-green-700 text-white py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 drop-shadow-lg">
            ❓ Questions & Réponses
          </h1>
          <p className="text-xl sm:text-2xl text-green-100 max-w-3xl mx-auto drop-shadow-md">
            Posez vos questions islamiques et obtenez des réponses détaillées de nos experts
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className={`grid grid-cols-1 ${user && myQuestions.length > 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-1 max-w-4xl mx-auto'} gap-6`}>
          {/* Colonne principale - Liste des questions */}
          <div className={`${user && myQuestions.length > 0 ? 'lg:col-span-2' : ''} space-y-6`}>
            {/* Message de succès */}
            {success && (
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 animate-slide-down">
                <p className="text-green-700 font-medium flex items-center space-x-2">
                  <span>✅</span>
                  <span>Votre question a été envoyée avec succès ! Un administrateur y répondra bientôt.</span>
                </p>
              </div>
            )}

            {/* Recherche */}
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher une question ou une réponse..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Bouton pour poser une question */}
            <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl shadow-xl p-6 border-2 border-green-200 hover:border-green-300 transition-all duration-300">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center space-x-2">
                    <span className="text-3xl">💬</span>
                    <span>Poser une question</span>
                  </h2>
                  <p className="text-gray-600">
                    {user 
                      ? 'Partagez votre question avec notre communauté. Un administrateur y répondra bientôt.'
                      : 'Posez votre question anonymement. Un administrateur y répondra bientôt.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowForm(!showForm)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform shadow-lg ${
                    showForm
                      ? 'bg-gray-600 hover:bg-gray-700 text-white'
                      : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white hover:scale-105'
                  }`}
                >
                  {showForm ? '✕ Fermer' : '❓ Poser une question'}
                </button>
              </div>

              {/* Formulaire de question */}
              {showForm && (
                <form onSubmit={handleSubmit} className="mt-6 space-y-4 animate-slide-down">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Votre question * <span className="text-gray-500">(minimum 10 caractères)</span>
                    </label>
                    <textarea
                      value={formData.question}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      rows={5}
                      className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all resize-none ${
                        errors.question ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                      }`}
                      placeholder="Posez votre question islamique ici... (ex: Quelle est la signification de...)"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        {errors.question && (
                          <p className="text-sm text-red-600">{errors.question}</p>
                        )}
                      </div>
                      <p className={`text-xs ${formData.question.length < 10 ? 'text-gray-400' : 'text-green-600'}`}>
                        {formData.question.length} caractères
                      </p>
                    </div>
                  </div>

                  {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-700 text-sm">{errors.submit}</p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-4 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setFormData({ question: '' });
                        setErrors({});
                      }}
                      className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-medium transition-all duration-300 transform hover:scale-105"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !formData.question.trim() || formData.question.trim().length < 10}
                      className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Envoi...</span>
                        </>
                      ) : (
                        <>
                          <span>✅</span>
                          <span>Envoyer la question</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Liste des questions en format tableau */}
            <div className="bg-white rounded-xl shadow-xl border-2 border-gray-200 overflow-hidden">
              <div className="p-6 border-b-2 border-gray-200 bg-gradient-to-r from-green-50 to-white">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
                  <span>📚</span>
                  <span>Questions & Réponses</span>
                  <span className="text-lg text-gray-600 font-normal">({filteredQuestions.length})</span>
                </h2>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                </div>
              ) : filteredQuestions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-medium">Aucune question trouvée</p>
                  <p className="text-sm mt-2">
                    {searchQuery ? 'Essayez de modifier vos critères de recherche' : 'Soyez le premier à poser une question !'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredQuestions.map((question, index) => {
                    const answered = hasAnswer(question);
                    return (
                      <div
                        key={question.id}
                        className="p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {/* Numéro - Les nouvelles questions en haut avec le numéro le plus élevé */}
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                            {totalCount - index - (currentPage - 1) * 10}
                          </div>

                          {/* Logo Hanafia */}
                          <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-green-500 overflow-hidden shadow-md">
                            <img
                              src="/logo.jpg"
                              alt="Hanafia Logo"
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Question */}
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-800 font-semibold text-base mb-1 line-clamp-2">
                              {question.question}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              {question.is_my_question && (
                                <>
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-semibold">
                                    ✨ Ma question
                                  </span>
                                  <span>•</span>
                                </>
                              )}
                              <span>{formatDate(question.created_at)}</span>
                            </div>
                          </div>

                          {/* Statut et Bouton */}
                          <div className="flex-shrink-0 flex items-center gap-3">
                            {answered ? (
                              <>
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold whitespace-nowrap">
                                  ✅ Répondu
                                </span>
                                <button
                                  onClick={() => openDetailsModal(question)}
                                  className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-medium transition-all duration-300 transform hover:scale-105 shadow-md"
                                >
                                  Détails
                                </button>
                              </>
                            ) : (
                              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold whitespace-nowrap">
                                ⏳ En attente
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-6 border-t border-gray-200 flex justify-center items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ← Précédent
                  </button>
                  <span className="px-4 py-2 text-gray-700">
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Colonne latérale - Mes Questions (si connecté) */}
          {user && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 sticky top-24">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center space-x-2">
                  <span>👤</span>
                  <span>Mes Questions</span>
                </h3>

                {loadingMyQuestions ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  </div>
                ) : myQuestions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm mb-2">Vous n'avez pas encore posé de question</p>
                    <a
                      href="/faq"
                      className="inline-block mt-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                    >
                      Poser une question →
                    </a>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {myQuestions.map((question) => (
                      <div
                        key={question.id}
                        onClick={async () => {
                          try {
                            const fullQuestion = await publicQuestionsService.getQuestionById(question.id);
                            setSelectedQuestion(fullQuestion);
                            setShowDetailsModal(true);
                          } catch (error) {
                            console.error('Erreur lors du chargement des détails:', error);
                          }
                        }}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all cursor-pointer"
                      >
                        <p className="text-gray-800 font-medium text-sm mb-2 line-clamp-2">
                          {question.question}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{formatDate(question.created_at)}</span>
                          {question.answer || question.answer_video ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                              ✅ Répondu
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-semibold">
                              ⏳ En attente
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modale Détails */}
      {showDetailsModal && selectedQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-green-700 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold flex items-center space-x-2">
                  <span>📋</span>
                  <span>Détails de la Question</span>
                </h2>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedQuestion(null);
                  }}
                  className="text-white hover:text-gray-200 text-2xl transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Question */}
              <div className="bg-gray-50 rounded-xl p-5 border-l-4 border-green-500">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold text-lg">
                    ❓
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Question</h3>
                    <p className="text-gray-700 leading-relaxed">{selectedQuestion.question}</p>
                    <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                      {selectedQuestion.is_my_question && (
                        <>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                            ✨ Ma question
                          </span>
                          <span>•</span>
                        </>
                      )}
                      <span>📅 {formatDate(selectedQuestion.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Réponse */}
              {hasAnswer(selectedQuestion) ? (
                <div className="bg-green-50 rounded-xl p-5 border-l-4 border-blue-500">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                      ✅
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center space-x-2">
                        <span>Réponse de l'Administration</span>
                        {selectedQuestion.answered_by_name && (
                          <span className="text-sm font-normal text-gray-600">
                            par {selectedQuestion.answered_by_name}
                          </span>
                        )}
                      </h3>

                      {/* Réponse texte */}
                      {selectedQuestion.answer && (
                        <div className="mb-4">
                          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {selectedQuestion.answer}
                          </p>
                        </div>
                      )}

                      {/* Réponse vidéo */}
                      {selectedQuestion.answer_video && (
                        <div className="mt-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">📹 Réponse vidéo :</h4>
                          <video
                            controls
                            className="w-full rounded-lg shadow-lg"
                            src={selectedQuestion.answer_video}
                          >
                            Votre navigateur ne supporte pas la lecture de vidéos.
                          </video>
                        </div>
                      )}

                      {selectedQuestion.answered_at && (
                        <p className="text-xs text-gray-500 mt-3">
                          Répondu le {formatDate(selectedQuestion.answered_at)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 rounded-xl p-5 border-l-4 border-yellow-400">
                  <p className="text-yellow-700 flex items-center space-x-2 font-medium">
                    <span className="text-lg animate-pulse">⏳</span>
                    <span>En attente de réponse de l'administration</span>
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedQuestion(null);
                }}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
